// /finance?tab=invoices (finance-real, the user 2026-10-22: "all the invoices for all clients", then "incoming invoice and
// outgoing invoice"): every client's invoice and every team invoice in one table. The Type column tells them apart: "To
// client" is a client invoice, money in; "From team" is one person's invoice to the agency, their claims, money out (it
// replaces Pay). The header's right holds the shared status tabs, Draft | Sent or received | Overdue | Partly paid | Paid
// with both kinds' counts (`show`, Sent or received by default while it has any, else the first status with any, showTab;
// DD-062): a team invoice's Draft lists under Draft,
// Received under Sent or received, Paid under Paid, and none is ever overdue or partly paid. Each row is named as
// everywhere (invoiceName), whose it is in the Who column alone, over its date line and count ("Overdue · sent 18 Sep 2026
// · 3 jobs", invoiceDateLine); its jobs or claims are its drawer's. The footer gives each kind's totals as their own items,
// never added together, the server's (TD-102). A client invoice's row opens the client page's invoice drawer, which the
// page mounts on every tab (AgencyJobInvoiceDrawer: `modal=job-invoice&invoice=`), a team invoice's the team invoice drawer (`modal=team-invoice&teamInvoice=`, the page's),
// each status the shared status dropdown on the row and in the drawer: Paid opens the payment or payout step
// (`confirm=paid`). The header's New invoice (AgencyInvoicesMenu, the shared create button with a menu, DD-056) makes
// either: New invoice picks the client first, then the client page's New invoice of its done and active jobs; New team invoice
// picks the person, then lists their owed claims to tick (NewTeamInvoiceDrawer). A client invoice's Draft or Sent picked
// on its row sends a request key (useRequestKeys), as on the client page.
import { useState } from "react";
import { TablePageBody } from "@/components/PageLayout/TablePageBody";
import { BadgeDot } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { DataTable, type DataColumn } from "@/components/ui/DataTable";
import { FormField } from "@/components/ui/FormField";
import { MoneyText } from "@/components/ui/MoneyText";
import { OptionDropdown } from "@/components/ui/OptionDropdown";
import { TabGroup, type TabGroupConfig } from "@/components/ui/TabGroup";
import { ENTITY_CONFIG, EntityCreateMenu } from "@/core/entityConfig";
import type { TotalItem } from "../components/TotalsFooter";
import { Required } from "../components/Required";
import { TabPending } from "../components/TabPending";
import { TotalsFooter } from "../components/TotalsFooter";
import { FinanceDrawer } from "../components/FinanceDrawer";
import { invoiceTotalsOf } from "../clientPage/ClientFinancePage";
import { InvoiceStatusBadge, NewJobInvoiceDrawer, toBill } from "../clientPage/JobInvoiceDrawers";
import { JOB_INVOICE_STATES, JOB_INVOICE_STATUSES, TEAM_INVOICE_STATES } from "../clientPage/lineJobText";
import { useFinanceParams, type ParamPatch } from "../hooks/useFinanceParams";
import { useRequestKeys } from "../hooks/useRequestKeys";
import type { AgencyInvoiceRow, JobInvoiceStatus, TeamInvoiceStatus, TeamInvoiceTotals } from "../types";
import { invoiceDueNote } from "../utils/flows";
import { awaitsPayment, claimsCount, invoiceDateLine, invoiceName, jobsCount, withCount } from "../utils/names";
import { showTab } from "../utils/showTabs";
import { STATUS_TRACK } from "./AgencyJobsTab";
import { useAgencyInvoicesShowTabs } from "./hooks/useAgencyInvoicesShowTabs";
import { useAgencyInvoicesTab } from "./hooks/useAgencyInvoicesTab";
import { NewTeamInvoiceDrawer } from "./NewTeamInvoiceDrawer";
import { TeamInvoiceStatusBadge } from "./TeamInvoiceDrawer";

const INVOICE = ENTITY_CONFIG.invoice;
const TEAM = ENTITY_CONFIG.teamInvoice;
const invoiceWord = INVOICE.label.toLowerCase();
const CLIENT = ENTITY_CONFIG.client;
// The user's job, a line item, rides main's `contract` key (the entityConfig shadow).
const JOB = ENTITY_CONFIG.contract;

/** This tab's drawer params, cleared whenever it opens another. */
const NO_DRAWER: ParamPatch = { modal: null, lineJob: null, invoice: null, confirm: null, client: null, teamInvoice: null, stack: null, person: null };

/** The tab shown: the URL's, else Sent or received while it has invoices, else the first status with any (showTab). */
export const toInvoiceShow = (value: string | null, counts?: Record<JobInvoiceStatus, number>): JobInvoiceStatus =>
  showTab(value, JOB_INVOICE_STATUSES, "sent", counts ? (s) => counts[s] : null);

/** The shared tabs' labels: Sent lists the team's Received too. */
const TAB_LABEL: Record<JobInvoiceStatus, string> = {
  draft: JOB_INVOICE_STATES.draft.label,
  sent: "Sent or received",
  overdue: JOB_INVOICE_STATES.overdue.label,
  partial: JOB_INVOICE_STATES.partial.label,
  paid: JOB_INVOICE_STATES.paid.label,
};

/** What each kind is, in the Type column: which way the money goes. */
const KIND_LABEL: Record<AgencyInvoiceRow["kind"], string> = { client: `To ${CLIENT.label.toLowerCase()}`, team: "From team" };

const EMPTY: Record<JobInvoiceStatus, string> = {
  draft: `No draft ${invoiceWord}. ${INVOICE.createLabel} or ${TEAM.createLabel.toLowerCase()} is under ${INVOICE.createLabel} at the top right.`,
  sent: `No ${invoiceWord} waiting to be paid, and no ${TEAM.label.toLowerCase()} waiting to be paid out.`,
  overdue: `No ${invoiceWord} overdue: none sent more than 30 days ago still waits for money.`,
  partial: `No ${invoiceWord} partly paid.`,
  paid: `No ${invoiceWord} paid yet.`,
};

/** A team invoice's totals under a tab, as their own items: money out, never added to the clients' money in. */
function teamItemsOf(t: TeamInvoiceTotals, tab: JobInvoiceStatus): TotalItem[] {
  if (t.count === 0) return [];
  if (tab === "draft") return [{ label: `${KIND_LABEL.team}, drafts`, amountMinor: t.totalMinor }];
  if (tab === "paid") return [{ label: `${KIND_LABEL.team}, paid out`, amountMinor: t.paidMinor }];
  return [{ label: `${KIND_LABEL.team}, to pay out`, amountMinor: t.dueMinor }];
}

/** What a sent one still asks, when some of it arrived. */
const noteOf = (row: AgencyInvoiceRow): string | null => {
  if (row.kind === "team") return null;
  const i = row.invoice;
  return awaitsPayment(i.status) && i.dueMinor !== i.invoiceMinor ? invoiceDueNote(i.receivedMinor, i.dueMinor, "EUR") : null;
};

/** Its one name (invoiceName): whose it is is the Who column's, never repeated in the name. */
const titleOf = (row: AgencyInvoiceRow): string => (row.kind === "client" ? invoiceName({ kind: "client", ...row.invoice }) : row.invoice.title);
/** What it holds, in each kind's word: "2 jobs", "3 claims". */
const countOf = (row: AgencyInvoiceRow): string => (row.kind === "client" ? jobsCount(row.invoice.jobIds.length) : claimsCount(row.invoice.claims.length));
/** Its date line and count, as every invoice reads (invoiceDateLine): "Overdue · sent 18 Sep 2026 · 3 jobs". Its jobs or
 * claims are its drawer's. */
const dateLineOf = (row: AgencyInvoiceRow): string =>
  withCount(row.kind === "client" ? invoiceDateLine({ kind: "client", ...row.invoice }) : invoiceDateLine({ kind: "team", ...row.invoice }), countOf(row));
const whoOf = (row: AgencyInvoiceRow): string => (row.kind === "client" ? row.invoice.client.name : row.invoice.person.name);
const lookOf = (row: AgencyInvoiceRow) => (row.kind === "client" ? JOB_INVOICE_STATES[row.invoice.status] : TEAM_INVOICE_STATES[row.invoice.status]);

/** The header's status tabs, the client page's Invoices tabs: each status with both kinds' count. */
export function AgencyInvoicesShowTabs({ config }: { config: TabGroupConfig }) {
  const { params, set } = useFinanceParams();
  const data = useAgencyInvoicesShowTabs().data;
  return (
    <TabGroup
      items={JOB_INVOICE_STATUSES.map((s) => ({ id: s, label: TAB_LABEL[s], badge: data ? data.counts[s] : undefined, className: "shrink-0" }))}
      activeId={toInvoiceShow(params.get("show"), data?.counts)}
      onTabChange={(id) => set({ show: id }, true)}
      config={config}
      className={STATUS_TRACK}
    />
  );
}

/** The header's create at the top right (DD-056): New invoice, a menu of the two kinds, each tagged with its Type column's
 * word and picking whom first. */
export function AgencyInvoicesMenu({ onOpen }: { onOpen?: () => void }) {
  const { set } = useFinanceParams();
  const start = (modal: string) => {
    onOpen?.();
    set({ ...NO_DRAWER, modal });
  };
  return (
    <EntityCreateMenu
      label={INVOICE.createLabel}
      items={[
        { entity: "invoice", badge: KIND_LABEL.client, onClick: () => start("new-client-invoice") },
        { entity: "teamInvoice", badge: KIND_LABEL.team, onClick: () => start("new-team-invoice") },
      ]}
    />
  );
}

/** The first step of a new invoice from the ⋮: whom it is for or from, one pick. */
function PickFirstDrawer({ title, label, hint, options, action, onPick, onClose }: {
  title: string;
  label: string;
  hint: string;
  options: { id: string; name: string }[];
  action: string;
  onPick: (id: string) => void;
  onClose: () => void;
}) {
  const [picked, setPicked] = useState<string | null>(null);
  return (
    <FinanceDrawer
      title={title}
      onClose={onClose}
      footer={
        <Button disabled={picked === null} onClick={() => picked && onPick(picked)}>
          {action}
        </Button>
      }
    >
      <div className="space-y-3 text-sm">
        <FormField label={<Required>{label}</Required>} hint={hint}>
          <OptionDropdown variant="input" ariaLabel={label} value={picked} placeholder={`Pick the ${label.toLowerCase()}`} options={options.map((o) => ({ value: o.id, label: o.name }))} onChange={setPicked} />
        </FormField>
      </div>
    </FinanceDrawer>
  );
}

interface Props {
  /** Called as a row opens a drawer, so the page gives focus back to it once the drawers close (useOpenerFocus). */
  onOpen?: () => void;
}

export function AgencyInvoicesTab({ onOpen }: Props) {
  const { params, set, closeModals } = useFinanceParams();
  const modal = params.get("modal");
  const newForClient = modal === "new-client-invoice" ? params.get("client") : null;
  const { invoicesQuery, clientQuery, setInvoiceStatus, setTeamStatus } = useAgencyInvoicesTab(newForClient);
  const data = invoicesQuery.data;
  const show = toInvoiceShow(params.get("show"), data?.counts);
  const rows = data?.rows ?? [];
  const clientJobs = clientQuery.data?.jobs ?? [];
  const go = (patch: ParamPatch, replace?: boolean) => {
    onOpen?.();
    set({ ...NO_DRAWER, ...patch }, replace);
  };
  const close = () => closeModals(NO_DRAWER);
  const keys = useRequestKeys();

  // A row's status pick: Paid records a payment, or the payout, in the drawer; Partly paid is a payment's.
  const pickClient = (id: string, picked: JobInvoiceStatus) => {
    if (picked === "paid") return go({ modal: "job-invoice", invoice: id, confirm: "paid" });
    if (picked !== "draft" && picked !== "sent") return;
    const scope = `${id}:${picked}`;
    setInvoiceStatus.mutate({ invoiceId: id, input: { status: picked, requestId: keys.keyFor(scope) } }, { onSuccess: () => keys.settled(scope), onError: (error) => keys.settled(scope, error) });
  };
  const pickTeam = (id: string, picked: TeamInvoiceStatus) => {
    if (picked === "paid") return go({ modal: "team-invoice", teamInvoice: id, confirm: "paid" });
    setTeamStatus.mutate({ invoiceId: id, input: { status: picked } });
  };
  const loadingOf = (row: AgencyInvoiceRow): boolean =>
    row.kind === "client" ? setInvoiceStatus.isPending && setInvoiceStatus.variables?.invoiceId === row.invoice.id : setTeamStatus.isPending && setTeamStatus.variables?.invoiceId === row.invoice.id;

  const columns: DataColumn<AgencyInvoiceRow>[] = [
    {
      key: "invoice", label: INVOICE.label, truncates: true,
      render: (r) => (
        <span className="flex items-center gap-2 min-w-0">
          <BadgeDot tone={lookOf(r).tone} label={lookOf(r).label} className="sm:hidden" />
          <span className="flex flex-col min-w-0">
            <span className="font-medium text-gray-900 truncate">{titleOf(r)}</span>
            {/* DD-055: below md the Who and Type columns hide, and they lead the subline, the name first. */}
            <span className="text-xs text-gray-500 [overflow-wrap:anywhere]">
              <span className="md:hidden">{`${whoOf(r)} · ${KIND_LABEL[r.kind]} · `}</span>
              {dateLineOf(r)}
            </span>
          </span>
        </span>
      ),
    },
    { key: "kind", label: "Type", width: "hidden md:table-cell w-28", render: (r) => <span className={r.kind === "team" ? "text-gray-900" : "text-gray-600"}>{KIND_LABEL[r.kind]}</span> },
    { key: "who", label: "Who", width: "hidden md:table-cell w-44", render: (r) => <span className="block truncate text-gray-700">{whoOf(r)}</span> },
    {
      key: "state", label: "Status", width: "hidden sm:table-cell w-36",
      render: (r) =>
        r.kind === "client" ? (
          <InvoiceStatusBadge invoice={r.invoice} onPick={(s) => pickClient(r.invoice.id, s)} loading={loadingOf(r)} />
        ) : (
          <TeamInvoiceStatusBadge invoice={r.invoice} onPick={(s) => pickTeam(r.invoice.id, s)} loading={loadingOf(r)} />
        ),
    },
    {
      // DD-054: the invoice's total is Total, its drawer's word too; the name column is Invoice.
      key: "amount", label: "Total", align: "right", width: "w-32",
      render: (r) => {
        const note = noteOf(r);
        return (
          <span className="flex flex-col items-end">
            <MoneyText amountMinor={r.kind === "client" ? r.invoice.invoiceMinor : r.invoice.totalMinor} />
            {note && <span className="text-xs text-gray-500">{note}</span>}
          </span>
        );
      },
    },
  ];

  const shown = rows.filter((r) => r.tab === show);
  const team = data ? teamItemsOf(data.teamTotals[show], show) : [];
  const clients = data ? invoiceTotalsOf(data.clientTotals[show], show) : [];
  // Each kind's own items: named by kind when both are under the tab, so money in and money out never read as one sum.
  const footer = [...(team.length > 0 ? clients.map((i) => ({ ...i, label: `${KIND_LABEL.client}, ${i.label.toLowerCase()}` })) : clients), ...team];
  return (
    <>
      <TablePageBody>
        {!data ? (
          <TabPending failed={invoicesQuery.isError} message={`Could not load the ${INVOICE.plural.toLowerCase()}.`} retrying={invoicesQuery.isFetching} onRetry={() => void invoicesQuery.refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={shown}
            rowKey={(r) => `${r.kind}-${r.invoice.id}`}
            onRowClick={(r) => (r.kind === "client" ? go({ modal: "job-invoice", invoice: r.invoice.id }) : go({ modal: "team-invoice", teamInvoice: r.invoice.id }))}
            empty={EMPTY[show]}
            footer={footer.length > 0 ? <TotalsFooter items={footer} /> : undefined}
          />
        )}
      </TablePageBody>
      {data && modal === "new-client-invoice" && !newForClient && (
        <PickFirstDrawer
          title={INVOICE.createLabel}
          label={CLIENT.label}
          hint={`Then pick its ${JOB.plural.toLowerCase()} done or under way, as ${INVOICE.createLabel} does on its page.`}
          options={data.clients}
          action="Next"
          onPick={(id) => go({ modal: "new-client-invoice", client: id }, true)}
          onClose={close}
        />
      )}
      {newForClient && clientQuery.data && (
        <NewJobInvoiceDrawer
          key={newForClient}
          clientId={newForClient}
          titlePrefix={data?.clients.find((c) => c.id === newForClient)?.name}
          billable={clientJobs.filter(toBill)}
          onCreated={(id) => go({ modal: "job-invoice", invoice: id }, true)}
          onClose={close}
        />
      )}
      {data && modal === "new-team-invoice" && (
        <NewTeamInvoiceDrawer people={data.people} personId={params.get("person")} onOpened={(id) => go({ modal: "team-invoice", teamInvoice: id }, true)} onClose={close} />
      )}
    </>
  );
}
